/**
 * @file projectService.ts
 * @brief Project API service. Uses backend /task_management/projects; returns unwrapped payloads.
 */

import apiClient, { unwrapResponse } from './apiClient';
import { API_ENDPOINTS } from './endpoints';
import type {
  Project,
  ProjectStatus,
  ProjectFilters,
  PaginatedResponse,
  Milestone,
  ProjectHealthSummary,
} from '@/types';

function mapRawToProject(raw: Record<string, unknown>): Project {
  const milestones = Array.isArray(raw.milestones)
    ? (raw.milestones as Record<string, unknown>[]).map((m): Milestone => ({
        id: String(m.id),
        name: String(m.name ?? ''),
        description: m.description != null ? String(m.description) : undefined,
        dueDate: String(m.dueDate ?? ''),
        completedAt: m.completedAt != null ? String(m.completedAt) : undefined,
        isCompleted: Boolean(m.isCompleted ?? m.completedAt),
        taskIds: Array.isArray(m.taskIds) ? (m.taskIds as string[]) : [],
      }))
    : [];
  return {
    id: String(raw.id ?? ''),
    projRef: raw.projRef != null ? String(raw.projRef) : undefined,
    name: String(raw.name ?? ''),
    description: raw.description != null ? String(raw.description) : undefined,
    status: (raw.status as ProjectStatus) ?? 'planning',
    priority: (raw.priority as Project['priority']) ?? 'p3',
    projectType: raw.projectType as Project['projectType'],
    parentId: raw.parentId != null ? String(raw.parentId) : undefined,
    ownerId: String(raw.ownerId ?? ''),
    teamId: raw.teamId != null ? String(raw.teamId) : undefined,
    departmentId: raw.departmentId != null ? String(raw.departmentId) : undefined,
    startDate: String(raw.startDate ?? ''),
    endDate: String(raw.endDate ?? ''),
    estimatedMinutes: raw.estimatedMinutes != null ? Number(raw.estimatedMinutes) : undefined,
    budgetedMinutes: raw.budgetedMinutes != null ? Number(raw.budgetedMinutes) : undefined,
    actualMinutes: raw.actualMinutes != null ? Number(raw.actualMinutes) : undefined,
    taskIds: Array.isArray(raw.taskIds) ? (raw.taskIds as string[]) : [],
    taskCount: raw.taskCount != null ? Number(raw.taskCount) : undefined,
    completionPercentage: Number(raw.completionPercentage ?? 0),
    riskLevel: (raw.riskLevel as Project['riskLevel']) ?? 'low',
    riskNotes: raw.riskNotes != null ? String(raw.riskNotes) : undefined,
    milestones,
    tags: Array.isArray(raw.tags) ? (raw.tags as string[]) : [],
    color: raw.color != null ? String(raw.color) : undefined,
    createdAt: String(raw.createdAt ?? ''),
    updatedAt: String(raw.updatedAt ?? ''),
    createdBy: raw.createdBy != null ? String(raw.createdBy) : undefined,
    teamMembers: Array.isArray(raw.teamMemberIds) ? (raw.teamMemberIds as string[]) : [],
  } as Project;
}

function mapRawToMilestone(raw: Record<string, unknown>): Milestone {
  return {
    id: String(raw.id),
    name: String(raw.name ?? ''),
    description: raw.description != null ? String(raw.description) : undefined,
    dueDate: String(raw.dueDate ?? ''),
    completedAt: raw.completedAt != null ? String(raw.completedAt) : undefined,
    isCompleted: Boolean(raw.isCompleted ?? raw.completedAt),
    taskIds: Array.isArray(raw.taskIds) ? (raw.taskIds as string[]) : [],
  };
}

/** Normalize to date-only YYYY-MM-DD for MySQL DATE columns. */
function toDateOnly(value: string | undefined): string | undefined {
  if (value == null || value === '') return undefined;
  const s = typeof value === 'string' ? value : String(value);
  return s.slice(0, 10);
}

function buildListParams(filters?: ProjectFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters?.status?.length) params.set('status', filters.status[0]);
  if (filters?.ownerId) params.set('ownerId', filters.ownerId);
  if (filters?.teamId) params.set('teamId', filters.teamId);
  return params;
}

export const projectService = {
  async getProjects(
    filters?: ProjectFilters,
    page = 1,
    pageSize = 20
  ): Promise<PaginatedResponse<Project>> {
    const params = buildListParams(filters);
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    const url = `${API_ENDPOINTS.PROJECTS.LIST}?${params.toString()}`;
    const response = await apiClient.get(url);
    const data = unwrapResponse<{ items: Record<string, unknown>[]; total: number; page: number; pageSize: number; totalPages: number; hasMore: boolean }>(response);
    const items = Array.isArray(data?.items) ? (data.items as Record<string, unknown>[]).map(mapRawToProject) : [];
    return {
      items,
      total: Number(data?.total ?? 0),
      page: Number(data?.page ?? 1),
      pageSize: Number(data?.pageSize ?? 20),
      totalPages: Number(data?.totalPages ?? 1),
      hasMore: Boolean(data?.hasMore),
    };
  },

  async getProject(projectId: string): Promise<Project> {
    const response = await apiClient.get(API_ENDPOINTS.PROJECTS.GET(projectId));
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToProject(data);
  },

  async createProject(projectData: Partial<Project>): Promise<Project> {
    const body: Record<string, unknown> = {
      name: projectData.name ?? 'Untitled',
      description: projectData.description,
      status: projectData.status ?? 'planning',
      priority: projectData.priority ?? 'p3',
      projectType: projectData.projectType,
      parentId: projectData.parentId,
      ownerId: projectData.ownerId,
      teamId: projectData.teamId,
      departmentId: projectData.departmentId,
      startDate: toDateOnly(projectData.startDate) ?? new Date().toISOString().slice(0, 10),
      endDate: toDateOnly(projectData.endDate) ?? new Date().toISOString().slice(0, 10),
      riskLevel: projectData.riskLevel ?? 'low',
      riskNotes: projectData.riskNotes,
      color: projectData.color,
      teamMemberIds: (projectData as any).teamMemberIds,
    };
    const response = await apiClient.post(API_ENDPOINTS.PROJECTS.CREATE, body);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToProject(data);
  },

  async updateProject(projectId: string, projectData: Partial<Project>): Promise<Project> {
    const body: Record<string, unknown> = {
      name: projectData.name,
      description: projectData.description,
      status: projectData.status,
      priority: projectData.priority,
      projectType: projectData.projectType,
      ownerId: projectData.ownerId,
      teamId: projectData.teamId,
      departmentId: projectData.departmentId,
      startDate: projectData.startDate != null ? toDateOnly(projectData.startDate) : undefined,
      endDate: projectData.endDate != null ? toDateOnly(projectData.endDate) : undefined,
      riskLevel: projectData.riskLevel,
      riskNotes: projectData.riskNotes,
      color: projectData.color,
      teamMemberIds: (projectData as any).teamMemberIds,
    };
    const response = await apiClient.put(API_ENDPOINTS.PROJECTS.UPDATE(projectId), body);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToProject(data);
  },

  async deleteProject(projectId: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.PROJECTS.DELETE(projectId));
  },

  async getProjectChildren(projectId: string): Promise<Project[]> {
    const result = await this.getProjects(undefined, 1, 500);
    return result.items.filter((p) => p.parentId === projectId);
  },

  async getProjectTasks(projectId: string): Promise<{ id: string }[]> {
    const response = await apiClient.get(API_ENDPOINTS.PROJECTS.TASKS(projectId));
    const data = unwrapResponse<{ id: string }[]>(response);
    return Array.isArray(data) ? data : [];
  },

  async addTaskToProject(projectId: string, taskData: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await apiClient.post(API_ENDPOINTS.PROJECTS.ADD_TASK(projectId), { ...taskData, projectId });
    return unwrapResponse<Record<string, unknown>>(response);
  },

  async getMilestones(projectId: string): Promise<Milestone[]> {
    const response = await apiClient.get(API_ENDPOINTS.PROJECTS.MILESTONES(projectId));
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data) ? data.map(mapRawToMilestone) : [];
  },

  async addMilestone(projectId: string, milestoneData: Partial<Milestone>): Promise<Milestone> {
    const body: Record<string, unknown> = {
      name: milestoneData.name ?? 'New Milestone',
      description: milestoneData.description,
      dueDate: milestoneData.dueDate ?? new Date().toISOString().slice(0, 10),
    };
    const response = await apiClient.post(API_ENDPOINTS.PROJECTS.ADD_MILESTONE(projectId), body);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToMilestone(data);
  },

  async updateMilestone(
    projectId: string,
    milestoneId: string,
    milestoneData: Partial<Milestone>
  ): Promise<Milestone> {
    const body: Record<string, unknown> = {
      name: milestoneData.name,
      description: milestoneData.description,
      dueDate: milestoneData.dueDate,
      completedAt: milestoneData.completedAt ?? (milestoneData.isCompleted ? new Date().toISOString() : undefined),
    };
    const response = await apiClient.put(API_ENDPOINTS.PROJECTS.UPDATE_MILESTONE(projectId, milestoneId), body);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToMilestone(data);
  },

  async deleteMilestone(projectId: string, milestoneId: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.PROJECTS.DELETE_MILESTONE(projectId, milestoneId));
  },

  async getProjectStats(projectId: string): Promise<{
    totalTasks: number;
    completedTasks: number;
    overdueTasks?: number;
    totalHoursEstimated?: number;
    totalHoursLogged?: number;
    progress: number;
  }> {
    const response = await apiClient.get(API_ENDPOINTS.PROJECTS.STATS(projectId));
    const data = unwrapResponse<{ totalTasks?: number; completedTasks?: number }>(response);
    const totalTasks = Number(data?.totalTasks ?? 0);
    const completedTasks = Number(data?.completedTasks ?? 0);
    return {
      totalTasks,
      completedTasks,
      progress: totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0,
    };
  },

  async getProjectsHealth(): Promise<ProjectHealthSummary[]> {
    const response = await apiClient.get(API_ENDPOINTS.PROJECTS.HEALTH);
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data)
      ? data.map((r): ProjectHealthSummary => {
          const endDate = r.endDate != null ? new Date(String(r.endDate)) : null;
          const now = new Date();
          const daysRemaining = endDate ? Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : 0;
          return {
            projectId: String(r.projectId ?? ''),
            projectName: String(r.projectName ?? ''),
            progress: Number(r.progress ?? r.completionPercentage ?? 0),
            riskLevel: (r.riskLevel as ProjectHealthSummary['riskLevel']) ?? 'low',
            daysRemaining,
            isOverdue: endDate ? endDate < now : false,
            bottlenecks: Array.isArray(r.bottlenecks) ? (r.bottlenecks as string[]) : [],
          };
        })
      : [];
  },
};

export default projectService;
